// Server functions for Operation management
import { createServerFn } from '@tanstack/react-start'
import { db } from '@/src/server/db'
import { z } from 'zod'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { isAdmin } from '@/src/server/lib/access'
import { hasPermission } from '@/src/server/lib/permissions'
import { PERMISSIONS } from '@/permission.config'
import { serializeOperationRecord } from '@/src/server/lib/apiSerializers'
import { AuthorizationError } from '@/src/server/lib/appErrors'
import {
  assertCanListAllOperations,
  assertCanModifyDrawingOperations,
  assertCanModifyOperation,
  assertCanUseOperationType,
  assertCanViewDrawingOperations,
  requireUserIdFromSession,
  requireOrganizationId,
  requireResource,
} from '@/src/server/lib/operationAccess'
import { 
  OperationDataSchema, 
  FormSchemaDefinitionSchema, 
  defaultOperationFields,
  type FormSchemaDefinition
} from '@/shared/schemas/operation-data.zod'

// Key for storing operation form schema in EnvironmentSetting
const OPERATION_FORM_SCHEMA_KEY = 'operation_form_schema'

// Helper to get session from request
// --- Get Operation Form Schema ---
// Returns the current form schema for operations (can be customized by admin)
export const getOperationFormSchema = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => z.object({}).parse(data))
  .handler(async () => {
    // Try to get custom schema from EnvironmentSetting
    const setting = await db.environmentSetting.findUnique({
      where: { key: OPERATION_FORM_SCHEMA_KEY },
    })
    
    if (setting?.value) {
      try {
        return FormSchemaDefinitionSchema.parse(setting.value)
      } catch (e) {
        console.error('Failed to parse custom operation form schema, falling back to default. Error:', e)
        // Invalid schema, return default
      }
    }
    
    // Return default schema
    return {
      id: 'default-operation',
      name: 'Operation',
      description: 'Record operation data',
      fields: defaultOperationFields,
      groups: [],
      layout: 'multi-col' as const,
    }
  })

// --- Update Operation Form Schema (Admin only) ---
const UpdateOperationFormSchemaInput = z.object({
  schema: FormSchemaDefinitionSchema,
})

export const updateOperationFormSchema = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateOperationFormSchemaInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    
    // Only admin or org admin can update the form schema
    const canUpdate = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.ORG_ADMIN])
    if (!canUpdate) {
      throw new AuthorizationError('Only administrators can update the operation form schema')
    }
    
    // Upsert the schema in EnvironmentSetting
    await db.environmentSetting.upsert({
      where: { key: OPERATION_FORM_SCHEMA_KEY },
      create: {
        key: OPERATION_FORM_SCHEMA_KEY,
        value: data.schema as any,
        description: 'Dynamic form schema for operations',
      },
      update: {
        value: data.schema as any,
      },
    })
    
    return { success: true }
  })

// --- Create Operation ---
const CreateOperationInput = z.object({
  drawingId: z.string(),
  name: z.string().min(1),
  provider: z.string().optional(),
  source: z.string().optional(),
  data: OperationDataSchema.optional(),
  operationTypeId: z.string().optional(),
  schemaVersion: z.number().optional(),
})

export const createOperation = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CreateOperationInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = requireUserIdFromSession(session)
    
    // Verify user has access to the drawing
    const drawing = requireResource(await db.drawing.findUnique({
      where: { id: data.drawingId },
      select: { userId: true },
    }), 'Drawing not found')
    
    const isAdminUser = await isAdmin(session)
    assertCanModifyDrawingOperations({ drawing, userId, isAdmin: isAdminUser })

    // If operationTypeId is provided, get the type's current version and name
    let operationName = data.name
    let schemaVersion = data.schemaVersion
    
    if (data.operationTypeId) {
      const opType = requireResource(await db.operationType.findUnique({
        where: { id: data.operationTypeId },
        select: { name: true, version: true, labels: true, enabled: true, organizationId: true },
      }), 'Operation type not found')
      
      // Security: verify user can access this operation type
      // Users can only use global types (organizationId is null) or types from their own org
      const user = opType.organizationId && !isAdminUser
        ? await db.user.findUnique({
            where: { id: userId },
            select: { organizationId: true },
          })
        : null

      assertCanUseOperationType({
        operationType: opType,
        isAdmin: isAdminUser,
        userOrganizationId: user?.organizationId ?? null,
      })
      
      // Use the type's label (prefer 'en' as default, or fall back to type name)
      const labels = opType.labels as Record<string, string>
      operationName = labels?.en || opType.name
      schemaVersion = opType.version
    }
    
    const operation = await db.operation.create({
      data: {
        drawingId: data.drawingId,
        name: operationName,
        provider: data.provider || 'manual',
        source: data.source || 'user',
        data: data.data || {},
        operationTypeId: data.operationTypeId,
        schemaVersion: schemaVersion,
      },
    })
    
    return operation
  })

// --- Update Operation ---
const UpdateOperationInput = z.object({
  id: z.string(),
  name: z.string().min(1).optional(),
  provider: z.string().optional(),
  source: z.string().optional(),
  data: OperationDataSchema.optional(),
  operationTypeId: z.string().optional(),
  schemaVersion: z.number().optional(),
})

export const updateOperation = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateOperationInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = requireUserIdFromSession(session)
    
    // Get operation and its drawing
    const operation = requireResource(await db.operation.findUnique({
      where: { id: data.id },
      include: { drawing: { select: { userId: true } } },
    }), 'Operation not found')
    
    const isAdminUser = await isAdmin(session)
    assertCanModifyOperation({
      operation,
      userId,
      isAdmin: isAdminUser,
      unauthorizedMessage: 'Not authorized to update this operation',
    })
    
    const { id, ...updateData } = data
    const updated = await db.operation.update({
      where: { id },
      data: {
        ...(updateData.name && { name: updateData.name }),
        ...(updateData.provider && { provider: updateData.provider }),
        ...(updateData.source && { source: updateData.source }),
        ...(updateData.data && { data: updateData.data }),
        ...(updateData.operationTypeId !== undefined && { operationTypeId: updateData.operationTypeId }),
        ...(updateData.schemaVersion !== undefined && { schemaVersion: updateData.schemaVersion }),
      },
    })
    
    return updated
  })

// --- Delete Operation ---
const DeleteOperationInput = z.object({
  id: z.string(),
})

export const deleteOperation = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteOperationInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = requireUserIdFromSession(session)
    
    // Get operation and its drawing
    const operation = requireResource(await db.operation.findUnique({
      where: { id: data.id },
      include: { drawing: { select: { userId: true } } },
    }), 'Operation not found')
    
    const isAdminUser = await isAdmin(session)
    assertCanModifyOperation({
      operation,
      userId,
      isAdmin: isAdminUser,
      unauthorizedMessage: 'Not authorized to delete this operation',
    })
    
    await db.operation.delete({
      where: { id: data.id },
    })
    
    return { success: true }
  })

// --- List Operations for Drawing ---
const ListOperationsInput = z.object({
  drawingId: z.string(),
})

export const listOperations = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListOperationsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    // Verify access to drawing
    const drawing = requireResource(await db.drawing.findUnique({
      where: { id: data.drawingId },
      select: { userId: true, privacy: true },
    }), 'Drawing not found')
    
    const isAdminUser = await isAdmin(session)
    assertCanViewDrawingOperations({
      drawing,
      userId,
      isAdmin: isAdminUser,
      unauthorizedMessage: 'Not authorized to view operations for this drawing',
    })
    
    const operations = await db.operation.findMany({
      where: {
        drawingId: data.drawingId,
        deletedAt: null,
      },
      orderBy: { createdAt: 'desc' },
    })
    
    return operations.map(serializeOperationRecord)
  })

// --- Get Single Operation ---
const GetOperationInput = z.object({
  id: z.string(),
})

export const getOperation = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetOperationInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    const operation = requireResource(await db.operation.findUnique({
      where: { id: data.id },
      include: { drawing: { select: { userId: true, privacy: true } } },
    }), 'Operation not found')
    
    const isAdminUser = await isAdmin(session)
    assertCanViewDrawingOperations({
      drawing: operation.drawing,
      userId,
      isAdmin: isAdminUser,
      unauthorizedMessage: 'Not authorized to view this operation',
    })
    
    return serializeOperationRecord(operation)
  })

// --- List All Operations (Admin / Org-Admin) ---
const ListAllOperationsInput = z.object({
  drawingId: z.string().optional(),
  userId: z.string().optional(),
  operationTypeId: z.string().optional(),
  orgOnly: z.boolean().optional(), // When true, restrict to the caller's organization
  includeDeleted: z.boolean().optional().default(false),
  limit: z.number().min(1).max(100).optional(),
  offset: z.number().min(0).optional(),
})

export const listAllOperations = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListAllOperationsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const callerId = requireUserIdFromSession(session)

    const isAdminUser = await isAdmin(session)
    const canViewAll = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.ORG_ADMIN])
    assertCanListAllOperations(canViewAll)

    const limit = data.limit ?? 20
    const offset = data.offset ?? 0

    const where: Record<string, any> = data.includeDeleted ? {} : { deletedAt: null }

    if (data.drawingId) {
      where.drawingId = data.drawingId
    }

    if (data.userId) {
      where.drawing = { userId: data.userId }
    }

    if (data.operationTypeId) {
      where.operationTypeId = data.operationTypeId
    }

    // When org-only, restrict to drawings owned by users in the same organization
    if (data.orgOnly && !isAdminUser) {
      const caller = await db.user.findUnique({
        where: { id: callerId },
        select: { organizationId: true },
      })
      const callerOrganizationId = requireOrganizationId(caller?.organizationId)
      const orgUsers = await db.user.findMany({
        where: { organizationId: callerOrganizationId },
        select: { id: true },
      })
      const orgUserIds = orgUsers.map((u) => u.id)
      where.drawing = { ...where.drawing, userId: { in: orgUserIds } }
    }

    let operations: any[] = []
    let total = 0

    try {
      ;[operations, total] = await Promise.all([
        db.operation.findMany({
          where,
          include: {
            drawing: {
              select: {
                id: true,
                name: true,
                shareId: true,
                user: { select: { id: true, name: true, email: true } },
              },
            },
            operationType: {
              select: {
                id: true,
                name: true,
                labels: true,
                organizationId: true,
                organization: { select: { id: true, name: true } },
              },
            },
          },
          orderBy: { createdAt: 'desc' },
          take: limit,
          skip: offset,
        }),
        db.operation.count({ where }),
      ])
    } catch (error) {
      const isMissingColumn =
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        (error as { code?: string }).code === 'P2022'

      if (!isMissingColumn) {
        throw error
      }

      console.warn('Operation table schema mismatch detected (missing column). Returning empty operations list.')
    }

    return {
      operations: operations.map(serializeOperationRecord),
      total,
    }
  })
